import { ColumnsType } from 'antd/lib/table'
import Tag from 'src/components/common/tag/Tag'
import { Flex, Tooltip } from 'antd'
import SkillsPopover from './skillsPopover/SkillsPopover'
import { ExpertTableDataType } from 'src/types/api.types'
import { LifeCycleStage } from 'src/constants/experts.constants'
import styles from './TableColumn.module.less'

const getListOfSkills = (skillList: string[] | undefined, maxCharCount: number): string[] => {
    const skills = skillList?.sort()
    if (!skills) {
        return []
    }

    let remainingCharCount = maxCharCount
    let numOfSkillsToDisplay = 0

    for (const skill of skills) {
        const tagCharCount = 3 + skill.length
        if (remainingCharCount >= tagCharCount) {
            remainingCharCount -= tagCharCount
            numOfSkillsToDisplay++
        } else {
            break
        }
    }

    const numOfRemainingSkills = skills.length - numOfSkillsToDisplay
    if (numOfRemainingSkills === 0) {
        return skills
    }

    const plusTag = `+${numOfRemainingSkills}`
    const plusTagCharCount = 3 + plusTag.length
    const result = skills.slice(0, numOfSkillsToDisplay)

    if (remainingCharCount >= plusTagCharCount) {
        result.push(plusTag)
    } else if (numOfSkillsToDisplay > 0) {
        result.pop()
        result.push(`+${numOfRemainingSkills + 1}`)
    }

    return result
}

const renderSkills = (skills: string[] | undefined) => {
    const skillsToDisplay = getListOfSkills(skills || [], 52)
    return (
        <SkillsPopover skills={skills || []}>
            <Flex gap={4}>
                {skillsToDisplay.map((skill, index) => (
                    <Tag key={index} label={skill} color="cloud" textWeight="medium" isRounded />
                ))}
            </Flex>
        </SkillsPopover>
    )
}

const renderLifeCycleStage = (lifeCycleStage: string) => {
    switch (lifeCycleStage) {
        case LifeCycleStage.Unoccupied:
            return (
                <Tag
                    label={lifeCycleStage}
                    textWeight="medium"
                    color="green"
                    isUppercase
                    isRounded
                    fitWidth
                />
            )
        case LifeCycleStage.OccupiedAvailable:
            return (
                <Tag
                    label={lifeCycleStage}
                    textWeight="medium"
                    color="gold"
                    isUppercase
                    isRounded
                    fitWidth
                />
            )
        case LifeCycleStage.OccupiedUnAvailable:
            return <Tag label={lifeCycleStage} textWeight="medium" isUppercase isRounded fitWidth />
        case LifeCycleStage.IncompleteProfile:
            return (
                <Tag
                    label={lifeCycleStage}
                    textWeight="medium"
                    color="steel"
                    isUppercase
                    isRounded
                    fitWidth
                />
            )

        default:
            return null
    }
}

export const ExpertsTableColumn: ColumnsType<ExpertTableDataType> = [
    {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        width: 100,
        render: text => (
            <Tooltip title={text}>
                <div className={styles.ellipsis}>{text}</div>
            </Tooltip>
        ),
        ellipsis: true,
    },
    {
        title: 'Skills',
        dataIndex: 'skills',
        key: 'skills',
        width: 240,
        render: skills => renderSkills(skills),
        ellipsis: true,
    },
    {
        title: 'Languages',
        dataIndex: 'languages',
        key: 'languages',
        width: 125,
        render: text => (
            <Tooltip title={text}>
                <div className={styles.ellipsis}>{text}</div>
            </Tooltip>
        ),
        ellipsis: true,
    },
    {
        title: 'Exp. (years)',
        dataIndex: 'expInYears',
        key: 'expInYears',
        width: 70,
        ellipsis: true,
    },
    {
        title: 'Lifecycle stage',
        dataIndex: 'lifeCycleStage',
        key: 'lifeCycleStage',
        width: 125,
        render: lifeCycle => renderLifeCycleStage(lifeCycle),
        ellipsis: true,
    },
    {
        title: 'Actions',
        dataIndex: 'actions',
        key: 'actions',
        width: 55,
        align: 'center',
        ellipsis: true,
    },
]

export const AdditionalExpertsTableColumn: ColumnsType<ExpertTableDataType> = [
    {
        title: 'Location',
        dataIndex: 'location',
        key: 'location',
        width: 75,
        render: text => (
            <Tooltip title={text}>
                <div className={styles.ellipsis}>{text}</div>
            </Tooltip>
        ),
        ellipsis: true,
    },
    {
        title: 'Education degree',
        dataIndex: 'degree',
        key: 'degree',
        width: 100,
        render: text => (
            <Tooltip title={text}>
                <div className={styles.ellipsis}>{text}</div>
            </Tooltip>
        ),
        ellipsis: true,
    },
    {
        title: 'Occupation',
        dataIndex: 'occupation',
        key: 'occupation',
        width: 75,
        render: text => (
            <Tooltip title={text}>
                <div className={styles.ellipsis}>{text}</div>
            </Tooltip>
        ),
        ellipsis: true,
    },
]
